import { Link } from '@tanstack/react-router';
import { Building2, History, Layers, TriangleAlert, UserPlus } from 'lucide-react';
import { forwardRef, useState, type ReactNode } from 'react';
import {
  NOTE_COUNTER_FROM,
  NOTE_MAX_LENGTH,
  PREVIOUS_OUTCOME_LABELS,
  hrStarsOf,
  overBudgetLabel,
  type AnswerRow,
  type ReviewLine,
  type SortingCard,
} from '@nest-recruit/shared';
import { Chip } from '../../../components/Chip';
import { Textarea } from '../../../components/Input';
import { StarRating } from '../../../components/Stars';
import { ToneChip, ToneIcon } from '../../../components/Tone';
import { STAGE_TONES } from '../../../components/status-tones';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, orEmpty } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { useJob } from '../../jobs/api/jobs-api';
import { useRankingDetail } from '../../ranking/api/ranking-api';
import { ProfileBreakdownView } from '../../ranking/components/ProfileBreakdownView';
import { ScoreBreakdown } from '../../ranking/components/WhyThisScore';
import { saveRating } from '../../shortlist/api/shortlist-api';
import styles from './DecisionPanel.module.css';
import { useStageLabels } from '../../../lib/modules';

interface DecisionPanelProps {
  readonly card: SortingCard;
  readonly jobId: string;
  /** JB-13: the matrix lines ticked "HR review". */
  readonly reviewLines: readonly ReviewLine[];
  readonly note: string;
  readonly onNoteChange: (text: string) => void;
  readonly onNoteBlur: () => void;
}

/**
 * SCR-01's right region as HR's review card (R6, §L31): who this is, the profile's match and lines (ADR 0062; RNK-02's criteria breakdown for a job still ranked by them), HR's
 * 1–5 on the lines ticked for HR review — beside the score, never part of it — what they
 * answered, the flags, and HR's note, which the manager sees. Restricted fields arrive as `—`
 * from the server (§L23); nothing is hidden here.
 */
export const DecisionPanel = forwardRef<HTMLTextAreaElement, DecisionPanelProps>(function DecisionPanel({ card, jobId, reviewLines, note, onNoteChange, onNoteBlur }, noteRef): ReactNode {
  const { candidate, score } = card;
  return (
    <div className={styles.panel}>
      <header className={styles.identity}>
        <div className={styles.nameLine}>
          <ToneIcon tone={STAGE_TONES[card.stage]} />
          {/* A new tab, so the queue keeps its place (SCR-01 §3). */}
          <a className={styles.name} href={`/candidates/${candidate.id}`} target="_blank" rel="noreferrer">
            {candidate.fullName}
          </a>
          {candidate.doNotRehire && <Chip tone="danger">Do not rehire</Chip>}
          {card.reviewStatus === 'maybe' && <Chip tone="hold">Back from Maybe</Chip>}
        </div>
        <p className={styles.meta}>
          {[card.reference, `Applied ${formatDate(card.appliedAt)}`, card.sourceLabel].join(' · ')}
        </p>
        <p className={styles.meta}>
          {orEmpty(candidate.email)} · {orEmpty(candidate.phone)}
        </p>
      </header>

      <Flags card={card} />

      {score.profile ? (
        // ADR 0062, mockup M3: "Match 82 ★4 · Top fit" and the profile's lines; the knockout penalty is already taken off the match.
        <section className={styles.section} aria-label="Match">
          <h3 className={styles.heading}>Match</h3>
          <ProfileBreakdownView breakdown={score.profile} isQueueNoteShown />
        </section>
      ) : (
        <section className={styles.section} aria-label="Score">
          <ScoreSection card={card} jobId={jobId} />
          {score.isMissingMustHave && (
            <p className={styles.capped}>
              <TriangleAlert size={14} aria-hidden />
              Missing a must-have, so the score cannot go above 49.
            </p>
          )}
          {score.knockoutPenalty > 0 && <p className={styles.small}>Knockout penalty: −{score.knockoutPenalty} points.</p>}
        </section>
      )}

      {reviewLines.length > 0 ? <ReviewRatings key={card.applicationId} card={card} lines={reviewLines} /> : <NoReviewLines jobId={jobId} />}

      {card.formAnswers.length > 0 && (
        <section className={styles.section} aria-label="Answers">
          <h3 className={styles.heading}>Answers</h3>
          <dl className={styles.facts}>
            {card.formAnswers.map((row) => (
              <div key={row.label} className={styles.fact}>
                <dt>{row.label}</dt>
                <dd>
                  <AnswerValue row={row} />
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {card.questionAnswers.length > 0 && (
        <section className={styles.section} aria-label="Screening questions">
          <h3 className={styles.heading}>Screening questions</h3>
          <ul className={styles.questions}>
            {card.questionAnswers.map((row) => (
              <li key={row.questionId} className={row.isKnockout && row.didPass === false ? styles.questionFailed : styles.question}>
                <span className={styles.questionText}>{row.question}</span>
                <span className={styles.answer}>
                  {orEmpty(row.answer)}
                  {row.isKnockout && row.didPass === false && <Chip tone="danger">Failed</Chip>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.notes} aria-label="HR's note">
        <label htmlFor="sorting-note" className={styles.heading}>
          HR's note
        </label>
        <Textarea
          ref={noteRef}
          id="sorting-note"
          rows={3}
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          counterFrom={NOTE_COUNTER_FROM}
          placeholder="The manager sees this beside HR ★. Saved when you click away, and with your decision."
          onChange={(event) => onNoteChange(event.target.value)}
          onBlur={onNoteBlur}
        />
      </section>
    </div>
  );
});

/**
 * One answer. The expected salary above the job's budget top reads red with the candidate
 * pop-up's "N% over budget" chip (Redesign Group 3); the server leaves the percent out for a
 * viewer salaries are hidden from (#5), and a restricted answer is only ever `—`.
 */
export function AnswerValue({ row }: { readonly row: AnswerRow }): ReactNode {
  if (row.isRestricted) return <span title="Not visible to your role">—</span>;
  const percent = row.overBudgetPercent ?? null;
  if (percent === null) return orEmpty(row.value);
  return (
    <span className={styles.overBudget}>
      <b>{orEmpty(row.value)}</b>
      <ToneChip tone="urgent">{overBudgetLabel(percent)}</ToneChip>
    </span>
  );
}

/**
 * Redesign Group 3: with no matrix line ticked "HR review" there is nothing to rate; say so, and
 * point someone who may edit the job's setup at the Scoring matrix on its Interview step, where the lines are ticked.
 */
export function NoReviewLines({ jobId }: { readonly jobId: string }): ReactNode {
  const job = useJob(jobId);
  // Owner, 2026-11-01: the scoring matrix lives on the Interview step, so only a job with an interview round has one to tick.
  const canEditMatrix = job.data?.actions.includes('edit') === true && job.data.interviewRounds.length > 0;
  return (
    <section className={styles.section} aria-label="HR's ratings">
      <h3 className={styles.heading}>HR's ratings</h3>
      <p className={styles.small}>
        No lines ticked for HR review.
        {canEditMatrix && (
          <>
            {' '}
            <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'interview_1' }} className={styles.inlineLink}>
              Tick them on the Scoring matrix in Interview 1
            </Link>
          </>
        )}
      </p>
    </section>
  );
}

/** RNK-02's breakdown, prefetched with the card; the score alone for the moment before it lands. */
function ScoreSection({ card, jobId }: { readonly card: SortingCard; readonly jobId: string }): ReactNode {
  const detail = useRankingDetail(card.applicationId);
  if (detail.data?.applicationId === card.applicationId) return <ScoreBreakdown detail={detail.data} jobId={jobId} />;
  return (
    <div className={styles.scoreLine}>
      <span className={styles.scoreValue}>{card.score.matchScore ?? '—'}</span>
      <span className={styles.scoreOf}>out of 100{card.score.isProvisional ? ' · provisional' : ''}</span>
    </div>
  );
}

/**
 * F23.06: HR's 1–5 per line, saved the moment a star is picked and put back with the reason if
 * the server says no (§5a rule 1). HR ★ is their mean, shown beside the ATS score (§L31).
 */
function ReviewRatings({ card, lines }: { readonly card: SortingCard; readonly lines: readonly ReviewLine[] }): ReactNode {
  const show = useToasts((toasts) => toasts.show);
  const [ratings, setRatings] = useState<Readonly<Record<string, number>>>(card.review.ratings);
  const hrStars = hrStarsOf(
    lines.flatMap((line) => {
      const score = ratings[line.key];
      return score === undefined ? [] : [score];
    }),
  );

  function rate(line: ReviewLine, score: number | null): void {
    const before = ratings;
    const next = { ...ratings };
    if (score === null) delete next[line.key];
    else next[line.key] = score;
    setRatings(next);
    saveRating(card.applicationId, line.key, score)
      .then((saved) => setRatings(saved.ratings))
      .catch((error: unknown) => {
        setRatings(before);
        show({ tone: 'danger', message: `${line.label}: rating not saved`, detail: errorMessage(error) });
      });
  }

  return (
    <section className={styles.section} aria-label="HR's ratings">
      <div className={styles.ratingsHead}>
        <h3 className={styles.heading}>HR's ratings</h3>
        <span className={styles.hrStars}>HR ★ {hrStars === null ? '—' : hrStars.toFixed(1)}</span>
      </div>
      <ul className={styles.ratings}>
        {lines.map((line) => (
          <li key={line.key} className={styles.rating}>
            <span className={styles.ratingLabel}>{line.label}</span>
            <StarRating label={line.label} value={ratings[line.key] ?? null} onChange={(score) => rate(line, score)} hints={line.anchors} />
          </li>
        ))}
      </ul>
      <p className={styles.small}>These sit beside the ATS score and never change it.</p>
    </section>
  );
}

/** SCR-01 "Flags" — and §L14: an earlier application's outcome, never its score. */
function Flags({ card }: { readonly card: SortingCard }): ReactNode {
  const stageLabels = useStageLabels();
  const { flags } = card;
  const hasAny = flags.previous.length + flags.liveElsewhere.length + flags.pools.length > 0 || flags.referrerName !== null || card.score.isKnockoutFlagged;
  if (!hasAny) return null;
  return (
    <ul className={styles.flags} aria-label="Flags">
      {flags.previous.map((previous) => (
        <li key={`${previous.jobTitle}-${previous.appliedAt}`} className={styles.flag}>
          <History size={14} aria-hidden />
          <span>
            <strong>Applied before:</strong> {previous.jobTitle}, {formatDate(previous.appliedAt)} — {PREVIOUS_OUTCOME_LABELS[previous.outcome].toLowerCase()}
            {previous.outcome === 'in_progress' ? '' : ` at ${stageLabels[previous.finalStage].toLowerCase()}`}
          </span>
        </li>
      ))}
      {flags.liveElsewhere.length > 0 && (
        <li className={styles.flag}>
          <Building2 size={14} aria-hidden />
          <span>
            <strong>
              Active in {flags.liveElsewhere.length} other {flags.liveElsewhere.length === 1 ? 'pipeline' : 'pipelines'}:
            </strong>{' '}
            {flags.liveElsewhere.map((other) => `${other.ventureName} · ${stageLabels[other.stage]}`).join('; ')}
          </span>
        </li>
      )}
      {flags.pools.length > 0 && (
        <li className={styles.flag}>
          <Layers size={14} aria-hidden />
          <span>
            <strong>In talent pool:</strong> {flags.pools.join(', ')}
          </span>
        </li>
      )}
      {flags.referrerName && (
        <li className={styles.flag}>
          <UserPlus size={14} aria-hidden />
          <span>
            <strong>Referred by</strong> {flags.referrerName}
          </span>
        </li>
      )}
      {card.score.isKnockoutFlagged && (
        <li className={styles.flag}>
          <TriangleAlert size={14} aria-hidden />
          <span>A screening question asked for a closer look.</span>
        </li>
      )}
    </ul>
  );
}
